import type { PlatformExtensionRoute } from '@elmes/plugin-sdk';
import { matchPath } from 'react-router-dom';
import { joinCoreRoster, webExtensions } from './platformExtensions.js';

/**
 * Every route App.tsx declares, nested paths composed, the catch-all `*` aside; pinned against
 * App.tsx (core-page-routes-parity.test.ts). An extension route one of them matches refuses the build.
 */
export const CORE_ROUTE_PATTERNS: readonly string[] = [
  '/canvas/public/:token',
  '/login', '/register', '/forgot-password', '/reset-password/:token', '/verify-email/:token', '/oauth/callback', '/oauth/consent',
  '/dashboard', '/users', '/users/new', '/users/:id/edit', '/groups', '/groups/new', '/groups/:id/edit',
  '/files', '/files/:id/edit', '/canvas', '/canvas/:id', '/design-system', '/ui-system', '/pm',
  '/dm', '/dm/overview', '/dm/board', '/dm/inbox', '/dm/inbox/:id', '/dm/issues', '/dm/activity', '/dm/runs', '/dm/memory',
  '/dm/policies', '/dm/docs', '/dm/registry', '/dm/agents', '/dm/settings',
  '/dm-project', '/dm-inbox', '/dm-sprints', '/dm-runs', '/dm-memory', '/console', '/atlas/:slug',
  '/memory', '/memory/new', '/memory/:key/edit', '/memory/browser', '/system',
  '/settings', '/settings/security', '/settings/connections', '/profile', '/preview', '/ai/eval', '/ai/eval/profile/:id',
  '/ai', '/ai/profiles', '/ai/profiles/new', '/ai/profiles/:id', '/ai/chat', '/ai/chat/:id', '/ai/tools',
  '/ai/agents', '/ai/agents/:id', '/ai/agents/:id/permissions', '/ai/agents/:id/artifacts',
  '/ai/pipelines/runs/:pipelineId/:runId/live', '/ai/issues', '/ai/issues/new', '/ai/issues/:id', '/ai/notifications',
  '/ai/memory', '/ai/memory/forget', '/ai/tasks', '/ai/audit', '/ai/settings',
  '/ai/pipelines', '/ai/pipelines/new', '/ai/pipelines/templates', '/ai/pipelines/:id',
  '/ai/atlas', '/ai/atlas/graph', '/ai/atlas/modules', '/ai/atlas/data-model', '/ai/atlas/tools', '/ai/atlas/admin',
  '/ai/atlas/diff', '/ai/atlas/topology', '/ai/atlas/nodes/:id',
  '/ai/roadmap', '/ai/roadmap/inbox', '/ai/roadmap/kanban', '/ai/roadmap/matrix', '/ai/roadmap/tech-tree',
  '/ai/roadmap/timeline', '/ai/roadmap/sprint-planning', '/ai/roadmap/items/:id',
  '/ai/feed', '/ai/releases', '/ai/observability/traces', '/ai/observability/cost', '/ai/triggers',
  '/claude-cli', '/claude-cli/spawn', '/claude-cli/sessions/:uuid',
];

/**
 * Every page path the core api module manifests declare (App mounts them from the runtime module
 * list, which the build cannot see); pinned against those manifests (core-page-routes-parity.test.ts).
 */
export const CORE_MANIFEST_PAGE_PATTERNS: readonly string[] = [
  '/ai', '/ai/settings', '/ai-debate', '/chat', '/chat/admin', '/chat/admin/categories', '/chat/admin/channels',
  '/composer-telemetry', '/deployment', '/desktop', '/email-admin', '/email-admin/profiles', '/email-admin/addresses',
  '/email-admin/templates', '/email-admin/logs', '/email-admin/settings', '/notes', '/notes/:id', '/tmux',
  '/security', '/security/access', '/security/findings', '/security/posture', '/security/agents',
];

// Equal, `:param` or splat: whatever a core route would also match is a clash, never a shadow.
const coreRouteMatches = (pattern: string, path: string): boolean => matchPath({ path: pattern, end: true }, path) !== null;

joinCoreRoster('route', [...CORE_ROUTE_PATTERNS, ...CORE_MANIFEST_PAGE_PATTERNS], webExtensions.routes.map((route) => route.path), (path) => [path], coreRouteMatches);

/** The slot's direct routes, proven free of core's; App mounts them after every core route. */
export const EXTENSION_ROUTES: readonly PlatformExtensionRoute[] = webExtensions.routes;
